import { useCallback, useEffect, useState } from "react";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Flex, Icon, Icons, Spin, Switch, useToast } from "@ir/ui";
import { Permissions } from "@src/domain/permissions/Permissions";
import { UpdatePurchaserDTO } from "@src/domain/dto/purchasers";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";
import type { Purchaser } from "@src/domain/entities/purchaser";

interface PurchaserStatusToggleProps {
  purchaser: Purchaser;
}

export const PurchaserStatusToggle = ({ purchaser }: PurchaserStatusToggleProps) => {
  const { updatePurchaser, applyPurchaserUpdate } = usePurchasersData();
  const [pendingActive, setPendingActive] = useState(purchaser.purchaserIsActive);
  const [isUpdating, setIsUpdating] = useState(false);
  const { error: errorToast, success: successToast } = useToast();

  useEffect(() => {
    setPendingActive(purchaser.purchaserIsActive);
  }, [purchaser.purchaserIsActive]);

  const handleSwitchChange = useCallback(
    async (mode: boolean) => {
      setPendingActive(mode);
      setIsUpdating(true);
      try {
        const validated = UpdatePurchaserDTO.validate({
          purchaserId: purchaser.purchaserId,
          purchaserName: purchaser.purchaserName,
          isActive: mode,
        });
        if (!validated.success) {
          throw new Error("Validation failed");
        }

        const updated = await updatePurchaser(
          new UpdatePurchaserDTO(validated.data),
        );
        applyPurchaserUpdate(updated);
        successToast({
          content: `Purchaser ${purchaser.purchaserName} status updated successfully.`,
          icon: <Icon name={Icons.CheckCircle} />,
        });
      } catch (error) {
        console.error("Failed to update purchaser status", error);
        setPendingActive(purchaser.purchaserIsActive);
        errorToast({
          content: "Failed to update purchaser status. Please try again.",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      purchaser,
      updatePurchaser,
      applyPurchaserUpdate,
      successToast,
      errorToast,
    ],
  );

  return (
    <HasAnyPermission requiredPermissions={[Permissions.PURCHASERS_EDIT]}>
      <Flex align="center" gap={0.5}>
        {isUpdating && <Spin size="small" spinning />}
        <Switch
          size="small"
          isSelected={pendingActive}
          onChange={handleSwitchChange}
          isDisabled={isUpdating}
          aria-label="Purchaser status update"
        />
      </Flex>
    </HasAnyPermission>
  );
};
